import { HeadlineProps } from '@awazevr/design-system.components.text'
import { IPropertyCard } from '@awazevr/design-system.compositions.property-card'
import { TmsContextType } from '@awazevr/mfe-common.contexts.tms'

interface PodsPropertyDatalayerProps {
  properties: Omit<IPropertyCard, 'translations'>[]
  title?: string | HeadlineProps
}

interface PodsPropertyTileOnClickDatalayerProps {
  property: Omit<IPropertyCard, 'translations'>
  productNumberInRank: number
  title?: string | HeadlineProps
  tms?: TmsContextType
}

export const podsPropertyDatalayer = ({
  properties,
  title,
}: // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
PodsPropertyDatalayerProps) => {
  const datalayerProperties = properties?.reduce(
    (acc, obj, index) => {
      acc.product_bedrooms.push(obj.bedrooms?.toString() || '')
      acc.product_bathrooms.push(obj.bathrooms?.toString() || '')
      acc.product_list_price.push(
        obj.price?.toString() || obj.priceFrom?.toString() || '',
      )
      acc.product_key_features.push(obj.features?.[0]?.featureName || '')
      acc.product_grade.push(obj.grade?.toString() || '')
      acc.product_pets.push(obj.pets?.toString() || '')
      acc.product_parent_location.push(obj.location?.toLowerCase() || '')
      acc.product_name.push(obj.title?.toLowerCase())
      acc.product_parent_review_score.push(obj.score?.toString() || '')
      acc.product_parent_location_category.push(obj.rhs3?.toLowerCase() || '')
      acc.product_sku.push(obj.code?.toString() || '')
      acc.product_list_position.push(`${index++}`)
      acc.product_capactiy.push(obj.nights?.toString() || '')

      //  These fields are not available in the property card data so we return them the same way as the Search team does.
      acc.product_type.push('')
      acc.product_list_availability.push('')
      acc.product_list_strikethrough_price.push('')
      acc.product_list_badge.push('')
      acc.product_is_free_cancellation.push('')
      acc.product_parent_brand.push('')

      return acc
    },
    {
      product_bedrooms: [],
      product_bathrooms: [],
      product_list_price: [],
      product_key_features: [],
      product_grade: [],
      product_pets: [],
      product_parent_location: [],
      product_name: [],
      product_parent_review_score: [],
      product_parent_location_category: [],
      product_sku: [],
      product_list_position: [],
      product_type: [],
      product_capactiy: [],
      product_list_availability: [],
      product_list_strikethrough_price: [],
      product_list_badge: [],
      product_is_free_cancellation: [],
      product_parent_brand: [],
    },
  )

  return {
    event_name: 'ecommerce-product list',
    shop_list_title:
      typeof title === 'string'
        ? title?.toLowerCase() || ''
        : (title?.children as string)?.toLowerCase() || '',
    shop_number_of_products: `${properties.length}`,
    product_parent_name: datalayerProperties.product_name,
    product_parent_id: datalayerProperties.product_sku,
    ...datalayerProperties,
  }
}

const getVariableOrEmptyString = (variable: string | number | undefined) =>
  variable ? [variable?.toString().toLowerCase()] : ['']

export const podsPropertyTileOnClickDatalayer = ({
  property,
  productNumberInRank,
  title,
  tms,
}: PodsPropertyTileOnClickDatalayerProps): void => {
  tms?.updateDataLayer('link', 'ecommerce-product click', {
    event_name: 'ecommerce-product click',
    shop_list_title: getVariableOrEmptyString(
      typeof title === 'string' ? title : (title.children as string),
    ),
    product_parent_id: [property.code || ''],
    product_parent_name: getVariableOrEmptyString(property.title),
    product_parent_review_score: getVariableOrEmptyString(property.score),
    product_parent_location: getVariableOrEmptyString(property.location),
    product_parent_location_category: getVariableOrEmptyString(property.rhs3),
    product_sku: [property.code || ''],
    product_name: getVariableOrEmptyString(property.title),
    product_grade: getVariableOrEmptyString(property.grade),
    product_bedrooms: getVariableOrEmptyString(property.bedrooms),
    product_bathrooms: getVariableOrEmptyString(property.bathrooms),
    product_pets: getVariableOrEmptyString(property.pets),
    product_key_features: getVariableOrEmptyString(
      property.features?.[0]?.featureName,
    ),
    product_list_position: getVariableOrEmptyString(productNumberInRank),
    product_list_price: getVariableOrEmptyString(property.price),
    product_capacity: getVariableOrEmptyString(property.nights),

    //  These fields are not available in the property card data so we return them the same way as the Search team does.
    product_parent_brand: [''],
    product_type: [''],
    product_list_availability: [''],
    product_list_strikethrough_price: [''],
    product_list_badge: [''],
    product_is_free_cancellation: [''],
  })
}
